/**
 * Utilitaires d'export Excel et PDF
 * Support pour toutes les sections: PRODUITS, STOCK, FOURNISSEURS, CLIENTS, etc.
 */

import i18n from '../../i18n';

// Import des dépendances (à installer: npm install xlsx papaparse)
let XLSX, html2pdf;

try {
  XLSX = require('xlsx');
} catch (e) {
  console.warn(i18n.t('common.xlsx_not_installed'));
}

try {
  html2pdf = require('html2pdf.js');
} catch (e) {
  console.warn(i18n.t('common.jspdf_not_available'));
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildArabicPdfElement(title, data) {
  const headers = Object.keys(data[0] || {});
  const generatedOn = i18n.t('common.generated_on', {
    date: new Date().toLocaleDateString('ar-SA'),
    defaultValue: `تاريخ الإنشاء: ${new Date().toLocaleDateString('ar-SA')}`,
  });

  const headCells = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('');
  const bodyRows = data
    .map(
      (row) =>
        `<tr>${headers.map((h) => `<td>${escapeHtml(row[h] ?? '—')}</td>`).join('')}</tr>`,
    )
    .join('');

  const wrapper = document.createElement('div');
  wrapper.setAttribute('dir', 'rtl');
  wrapper.lang = 'ar';
  wrapper.style.cssText = [
    'font-family: Cairo, "IBM Plex Sans Arabic", sans-serif',
    'color: #2e452e',
    'background: #fff',
    'padding: 24px',
    'width: 794px',
    'box-sizing: border-box',
  ].join(';');

  wrapper.innerHTML = `
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
      .qawaem-pdf-title { font-size: 20px; font-weight: 700; margin: 0 0 8px; color: #2e452e; }
      .qawaem-pdf-meta { font-size: 12px; color: #475947; margin: 0 0 20px; }
      .qawaem-pdf-table { width: 100%; border-collapse: collapse; font-size: 11px; }
      .qawaem-pdf-table th {
        background: #2e452e;
        color: #fff;
        padding: 8px 10px;
        text-align: right;
        font-weight: 600;
        border: 1px solid #2e452e;
      }
      .qawaem-pdf-table td {
        padding: 7px 10px;
        border: 1px solid #d4e0d4;
        text-align: right;
        vertical-align: top;
        word-break: break-word;
      }
      .qawaem-pdf-table tr:nth-child(even) td { background: #f7faf7; }
    </style>
    <h1 class="qawaem-pdf-title">${escapeHtml(title)}</h1>
    <p class="qawaem-pdf-meta">${escapeHtml(generatedOn)}</p>
    <table class="qawaem-pdf-table">
      <thead><tr>${headCells}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
  `;

  return wrapper;
}

async function waitForPdfFonts(ms = 400) {
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* ignore */
    }
  }
  await new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Export vers Excel (.xlsx)
 */
export const exportToExcel = (data, filename, sheetName) => {
  const resolvedSheetName = sheetName || i18n.t('common.data_sheet');
  try {
    if (!XLSX) {
      throw new Error(i18n.t('common.xlsx_not_installed'));
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, resolvedSheetName);

    // Write file
    XLSX.writeFile(workbook, `${filename}.xlsx`);
    return true;
  } catch (error) {
    console.error(i18n.t('common.log_export_excel_error'), error);
    return false;
  }
};

/**
 * Export vers CSV
 */
export const exportToCSV = (data, filename) => {
  try {
    if (!data || data.length === 0) {
      throw new Error(i18n.t('common.no_export_data'));
    }

    // Convert to CSV
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const value = row[header];
        // Escape commas and quotes in values
        return typeof value === 'string' && (value.includes(',') || value.includes('"'))
          ? `"${value.replace(/"/g, '""')}"` 
          : value;
      }).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();
    return true;
  } catch (error) {
    console.error(i18n.t('common.log_export_csv_error'), error);
    return false;
  }
};

/**
 * Export vers PDF (Arabic/RTL via html2canvas + jsPDF)
 */
export const exportToPDF = async (data, filename, title) => {
  const resolvedTitle = title || i18n.t('common.export_data_title');
  try {
    if (!html2pdf) {
      throw new Error(i18n.t('common.export_error'));
    }

    if (!data || data.length === 0) {
      throw new Error(i18n.t('common.no_export_data'));
    }

    const element = buildArabicPdfElement(resolvedTitle, data);
    document.body.appendChild(element);

    try {
      await waitForPdfFonts();

      const colCount = Object.keys(data[0]).length;
      const orientation = colCount > 5 ? 'landscape' : 'portrait';

      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `${filename}.pdf`,
          html2canvas: {
            scale: 2,
            useCORS: true,
            letterRendering: true,
            scrollX: 0,
            scrollY: 0,
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(element)
        .save();

      return true;
    } finally {
      if (element.parentNode) {
        document.body.removeChild(element);
      }
    }
  } catch (error) {
    console.error(i18n.t('common.log_export_pdf_error'), error);
    return false;
  }
};

/**
 * Export multiple sheets to Excel
 */
export const exportMultipleSheetsToExcel = (sheets, filename) => {
  try {
    if (!XLSX) {
      throw new Error(i18n.t('common.xlsx_not_installed'));
    }

    const workbook = XLSX.utils.book_new();

    sheets.forEach(({ data, name }) => {
      if (data && data.length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, name);
      }
    });

    XLSX.writeFile(workbook, `${filename}.xlsx`);
    return true;
  } catch (error) {
    console.error(i18n.t('common.log_export_multi_sheets_error'), error);
    return false;
  }
};

// Export functions
export const exportFunctions = {
  exportToExcel,
  exportToCSV,
  exportToPDF,
  exportMultipleSheetsToExcel
};

// Main export function for compatibility
export const exportData = (data, filename, format = 'excel') => {
  switch (format.toLowerCase()) {
    case 'excel':
    case 'xlsx':
      return exportToExcel(data, filename);
    case 'csv':
      return exportToCSV(data, filename);
    case 'pdf':
      return exportToPDF(data, filename);
    default:
      return exportToExcel(data, filename);
  }
};
